import { useEffect, useMemo, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import useDocumentTitle from "../../../shared/customHooks/useDocumentTitle";
import AssetDetailsGrid from "../asset_UI_components/AssetDetailsGrid";
import {
  getInvestorsByContact,
  getEntitiesByInvestor,
  getReportDatesForAccountOrContact,
  getAssetStrategyList,
  getAssetPerformanceDetails,
} from "../services/assetOverviewService";
import "../styles/assetDetails.css";
import { LoadingSpinner } from "../../../shared/components/LoadingSpinner";
import ToolbarSelect from "./ToolbarSelect";

const get = (obj, camel, pascal) => obj?.[camel] ?? obj?.[pascal];

const mapStrategies = (list = []) => [
  { strategyId: "-1", strategyName: "--All--" },
  ...list.map((s) => ({
    strategyId: String(get(s, "strategyId", "StrategyId")),
    strategyName: get(s, "strategyName", "StrategyName"),
  })),
];

const AssetDetails = () => {
  useDocumentTitle("Asset Details");

  const { contactId: contactIdParam } = useParams();
  const contactId = Number(atob(contactIdParam));

  const [isLoading, setIsLoading] = useState(true);

  const [investors, setInvestors] = useState([]);
  const [entities, setEntities] = useState([]);
  const [reportDates, setReportDates] = useState([]);
  const [strategies, setStrategies] = useState([{ strategyId: "-1", strategyName: "--All--" }]);

  const [selectedInvestor, setSelectedInvestor] = useState("");
  const [selectedEntity, setSelectedEntity] = useState("");
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedStrategy, setSelectedStrategy] = useState("-1");

  const [gridRows, setGridRows] = useState([]);

  const selectedEntityObj = useMemo(
    () => entities.find((e) => String(get(e, "entityId", "EntityId")) === String(selectedEntity)),
    [entities, selectedEntity]
  );
  const investorOptions = useMemo(
    () =>
      investors.map((inv) => ({
        value: get(inv, "investorId", "InvestorId"),
        label: get(inv, "investorName", "InvestorName"),
      })),
    [investors]
  );
  const entityOptions = useMemo(
    () =>
      entities.map((ent) => ({
        value: get(ent, "entityId", "EntityId"),
        label: get(ent, "entityName", "EntityName"),
      })),
    [entities]
  );
  const strategyOptions = useMemo(
    () =>
      strategies.map((strategy) => ({
        value: strategy.strategyId,
        label: strategy.strategyName,
      })),
    [strategies]
  );
  const reportDateOptions = useMemo(
    () =>
      reportDates.map((d) => ({
        value: get(d, "reportDate", "ReportDate"),
        label: get(d, "reportDate", "ReportDate"),
      })),
    [reportDates]
  );

  const didInitRef = useRef(false);

  const accountSelected =
    Number(get(selectedEntityObj || {}, "entityTypeId", "EntityTypeId")) === 1;

  const resolveAccountContact = (entity) => {
    const entityTypeId = Number(get(entity || {}, "entityTypeId", "EntityTypeId") || 0);
    const entityId = Number(get(entity || {}, "entityId", "EntityId") || 0);
    return entityTypeId === 1
      ? { accountId: entityId, contactEntityId: 0 }
      : { accountId: 0, contactEntityId: entityId };
  };

  const loadGridData = async (reportingMonth, accountId, contactEntityId, strategyId) => {
    setIsLoading(true);
    try {
      const perfRes = await getAssetPerformanceDetails(
        reportingMonth,
        accountId,
        contactEntityId,
        strategyId
      );
      const rows = perfRes?.data || [];
      setGridRows(rows);
      return rows;
    } finally {
      setIsLoading(false);
    }
  };

  const loadDatesAndStrategy = async (entity) => {
    const { accountId, contactEntityId } = resolveAccountContact(entity);

    setIsLoading(true);
    try {
      const dateRes = await getReportDatesForAccountOrContact(accountId, contactEntityId);
      const dates = dateRes?.data || [];
      setReportDates(dates);

      if (!dates.length) {
        setSelectedDate("");
        setStrategies([{ strategyId: "-1", strategyName: "--All--" }]);
        setSelectedStrategy("-1");
        setGridRows([]);
        return;
      }

      const nextDate = get(dates[0], "reportDate", "ReportDate");
      setSelectedDate(nextDate);

      const strategyRes = await getAssetStrategyList(nextDate, accountId, contactEntityId);
      setStrategies(mapStrategies(strategyRes?.data || []));
      setSelectedStrategy("-1");

      await loadGridData(nextDate, accountId, contactEntityId, null);
    } finally {
      setIsLoading(false);
    }
  };

  const loadEntitiesAndDownstream = async (investorId) => {
    setIsLoading(true);
    try {
      const entRes = await getEntitiesByInvestor(investorId, contactId);
      const ent = entRes?.data || [];
      setEntities(ent);

      if (!ent.length) {
        setSelectedEntity("");
        setReportDates([]);
        setSelectedDate("");
        setStrategies([{ strategyId: "-1", strategyName: "--All--" }]);
        setSelectedStrategy("-1");
        setGridRows([]);
        return;
      }

      const firstEntity = ent[0];
      const firstEntityId = get(firstEntity, "entityId", "EntityId");
      setSelectedEntity(String(firstEntityId));

      await loadDatesAndStrategy(firstEntity);
    } finally {
      setIsLoading(false);
    }
  };

  const pickFirstInvestorWithData = async (invList) => {
    for (const inv of invList) {
      const invId = get(inv, "investorId", "InvestorId");
      const entRes = await getEntitiesByInvestor(invId, contactId);
      const ent = entRes?.data || [];
      if (!ent.length) continue;

      for (const entity of ent) {
        const { accountId, contactEntityId } = resolveAccountContact(entity);
        const dateRes = await getReportDatesForAccountOrContact(accountId, contactEntityId);
        const dates = dateRes?.data || [];
        if (!dates.length) continue;

        const firstDate = get(dates[0], "reportDate", "ReportDate");

        const rows = await getAssetPerformanceDetails(
          firstDate,
          accountId,
          contactEntityId,
          null
        );
        const data = rows?.data || [];

        if (data.length > 0) {
          return { investor: inv, ent,entity, dates, firstDate, data };
        }
      }
    }
    return null;
  };

  useEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true;

    if (!contactId || Number.isNaN(contactId)) return;

    const init = async () => {
      setIsLoading(true);
      const invRes = await getInvestorsByContact(contactId);
      const inv = invRes?.data || [];
      setInvestors(inv);

      if (!inv.length) {
        setGridRows([]);
        setIsLoading(false);
        return;
      }

      // pick first investor that has data
      const match = await pickFirstInvestorWithData(inv);

      if (match) {
        const invId = get(match.investor, "investorId", "InvestorId");
        const entId = get(match.entity, "entityId", "EntityId");
        setSelectedInvestor(String(invId));
        setEntities(match.ent);
        setSelectedEntity(String(entId));
       // console.log("Selected Entity ID", entId);
        setReportDates(match.dates);
        setSelectedDate(match.firstDate);
        setStrategies([{ strategyId: "-1", strategyName: "--All--" }]);
        setSelectedStrategy("-1");
        setGridRows(match.data);
        setIsLoading(false);
        return;
      }

      // fallback to first investor
      const firstInvestorId = get(inv[0], "investorId", "InvestorId");
      setSelectedInvestor(String(firstInvestorId));
      await loadEntitiesAndDownstream(firstInvestorId);
    };

    init();
  }, [contactId]);

  const onInvestorChange = async (e) => {
    const id = e.target.value;
    setSelectedInvestor(id);
    setSelectedEntity("");
    setSelectedDate("");
    setSelectedStrategy("-1");
    setGridRows([]);

    await loadEntitiesAndDownstream(id);
  };

  const onEntityChange = async (e) => {
    const id = e.target.value;
    setSelectedEntity(id);
    setSelectedDate("");
    setSelectedStrategy("-1");
    setGridRows([]);

    const entity = entities.find((x) => String(get(x, "entityId", "EntityId")) === String(id));
    if (entity) await loadDatesAndStrategy(entity);
  };

  const onDateChange = async (e) => {
    const date = e.target.value;
    setSelectedDate(date);
    setSelectedStrategy("-1");

    if (!selectedEntityObj || !date) {
      setGridRows([]);
      return;
    }

    const { accountId, contactEntityId } = resolveAccountContact(selectedEntityObj);
    const strategyRes = await getAssetStrategyList(date, accountId, contactEntityId);
    setStrategies(mapStrategies(strategyRes?.data || []));

    await loadGridData(date, accountId, contactEntityId, null);
  };

  const onStrategyChange = async (e) => {
    const strategy = e.target.value;
    setSelectedStrategy(strategy);

    if (!selectedDate || !selectedEntityObj) {
      setGridRows([]);
      return;
    }

    const { accountId, contactEntityId } = resolveAccountContact(selectedEntityObj);
    const strategyId = strategy === "-1" ? null : Number(strategy);

    await loadGridData(selectedDate, accountId, contactEntityId, strategyId);
  };

  const onGo = async () => {
    if (!selectedDate || !selectedEntityObj) return;
    const { accountId, contactEntityId } = resolveAccountContact(selectedEntityObj);
    const strategyId = selectedStrategy === "-1" ? null : Number(selectedStrategy);

    await loadGridData(selectedDate, accountId, contactEntityId, strategyId);
  };

  return (
    <div className="asset-details-page">
      <div className="action-bar">
        <div className="row">
          <div className="col-md-3">
            <label>Investor:</label>
            <ToolbarSelect
              value={selectedInvestor}
              onChange={(nextValue) => onInvestorChange({ target: { value: nextValue } })}
              options={investorOptions}
            />
          </div>

          <div className="col-md-4">
            <label>Entity:</label>
            <ToolbarSelect
              value={selectedEntity}
              onChange={(nextValue) => onEntityChange({ target: { value: nextValue } })}
              options={entityOptions}
            />
          </div>

          <div className="col-md-2">
            <label>Strategy:</label>
            <ToolbarSelect
              value={selectedStrategy}
              onChange={(nextValue) => onStrategyChange({ target: { value: nextValue } })}
              options={strategyOptions}
            />
          </div>

          <div className="col-md-2">
            <label>Date:</label>
            <ToolbarSelect
              value={selectedDate}
              onChange={(nextValue) => onDateChange({ target: { value: nextValue } })}
              options={reportDateOptions}
            />
          </div>

          <div className="col-md-1">
            <button type="button" className="btn2 btn-block focus-remove custom-fonts" onClick={onGo}>
              Go
            </button>
          </div>
        </div>
      </div>

      <hr />

      {isLoading ? (
        <LoadingSpinner />
      ) : (
         <>
         <div style={{ marginLeft: 12, fontWeight: "600", marginBottom: 8,color:"#10045a" }}>Asset Details</div>
        <AssetDetailsGrid
          data={gridRows}
          accountSelected={accountSelected}
          strategySelected={selectedStrategy}
        />
        </>
      )}
    </div>
  );
};

export default AssetDetails;
